if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();

      // Don't assume inner markup exists yet (theme/app may inject it later)
      this.button = null;
      this.listbox = null;
      this.options = [];
      this.nativeSelect = null;
      this.selectedOption = null;

      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;

      // bind once
      this._onKeydown = this.handleKeydown.bind(this);
      this._onMousedown = this.handleMousedown.bind(this);

      window.initLazyScript(this, this.init.bind(this));
    }

    /**
     * Find the best "selected option" element.
     * Always returns a real Element (.custom-select__option) or null.
     */
    resolveSelectedOption() {
      // preferred: aria-selected="true"
      const ariaSelected = this.querySelector('.custom-select__option[aria-selected="true"]');
      if (ariaSelected) return ariaSelected;

      // fallback: first option element
      const firstOption = this.querySelector('.custom-select__option');
      if (firstOption) return firstOption;

      return null;
    }

    /**
     * (Re)query required DOM nodes. Safe to call multiple times.
     */
    cacheDom() {
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.options = Array.from(this.querySelectorAll('.custom-select__option'));
      this.nativeSelect = this.id ? document.getElementById(`${this.id}-native`) : null;

      this.selectedOption = this.resolveSelectedOption();
    }

    init() {
      this.cacheDom();

      // If markup isn't ready yet, wait for it
      if (!this.button || !this.listbox || !this.options.length) {
        this.waitForMarkupAndInit();
        return;
      }

      this.swatches = this.options.some((el) => 'swatch' in el.dataset);
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;

      // Ensure selectedOption is valid
      this.selectedOption = this.resolveSelectedOption();

      // Bind listeners safely (avoid duplicates)
      this.removeEventListener('keydown', this._onKeydown);
      this.addEventListener('keydown', this._onKeydown);

      this.button.removeEventListener('mousedown', this._onMousedown);
      this.button.addEventListener('mousedown', this._onMousedown);

      // Measure after layout
      requestAnimationFrame(() => this.setButtonWidth());
    }

    /**
     * Wait for inner markup to exist (common in Shopify when sections/snippets render async)
     */
    waitForMarkupAndInit() {
      if (this._markupObserver) this._markupObserver.disconnect();

      this._markupObserver = new MutationObserver(() => {
        this.cacheDom();
        if (this.button && this.listbox && this.options.length) {
          this._markupObserver.disconnect();
          this._markupObserver = null;
          requestAnimationFrame(() => this.init());
        }
      });

      this._markupObserver.observe(this, { childList: true, subtree: true });
    }

    /**
     * Returns the next visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getNextVisibleSibling(elem) {
      if (!elem) return null;
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    /**
     * Returns the previous visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getPreviousVisibleSibling(elem) {
      if (!elem) return null;
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);

      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
    }

    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
    }

    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    handleMousedown(evt) {
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }

    handleMouseover(evt) {
      if (evt.target.matches('li')) {
        this.focusOption(evt.target);
      }
    }

    handleMouseleave() {
      this.focusOption(this.selectedOption);
    }

    handleClick(evt) {
      if (evt.target.tagName === 'LI') {
        this.selectOption(evt.target);
      }
    }

    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    handleKeyboardNav(evt) {
      let optionToFocus;

      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }

          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus);
          }
          break;

        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;

        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;

        default:
          optionToFocus = this.findOption(evt.key);
          if (optionToFocus) this.focusOption(optionToFocus);
          break;
      }
    }

    /**
     * Sets the button width to the same as the longest option,
     * to prevent the button width from changing depending on the option selected.
     */
    setButtonWidth() {
      // ✅ ensure correct element types
      if (!(this.button instanceof Element)) return;

      // re-resolve to be safe (markup can shift)
      const selected = this.resolveSelectedOption();
      if (!(selected instanceof Element)) return;

      // Ensure we measure an element with a rect function
      if (typeof selected.getBoundingClientRect !== 'function') return;

      const getHorizontalPadding = (el) => {
        if (!(el instanceof Element)) return 0;
        const elStyle = getComputedStyle(el);
        const pl = parseFloat(elStyle.paddingLeft) || 0;
        const pr = parseFloat(elStyle.paddingRight) || 0;
        return pl + pr;
      };

      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(selected);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(selected.getBoundingClientRect().width);

      this.button.style.setProperty(
        '--custom-select-button-width',
        `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`
      );

      // keep reference in sync
      this.selectedOption = selected;
    }

    showListbox() {
      if (!(this.button instanceof Element) || !(this.listbox instanceof Element)) return;

      this.listbox.hidden = false;
      this.listboxOpen = true;

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        this.selectedOption = this.resolveSelectedOption();
        this.focusOption(this.selectedOption);
        this.listbox.focus();

        this.addListboxOpenListeners();
      });
    }

    hideListbox() {
      if (!this.listboxOpen) return;
      if (!(this.button instanceof Element) || !(this.listbox instanceof Element)) return;

      this.listbox.hidden = true;
      this.listboxOpen = false;

      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }

      this.button.focus();
      this.removeListboxOpenListeners();
    }

    findOption(key) {
      this.searchString += key;

      if (this.searchTimer) clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);

      const matchingOption = (this.options || []).find((option) => {
        const label = (option.innerText || '').toLowerCase();
        return label.includes(this.searchString) && !option.classList.contains('is-disabled');
      });

      return matchingOption || null;
    }

    focusOption(option) {
      if (!(option instanceof Element) || !(this.listbox instanceof Element)) return;

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }

      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    selectOption(option) {
      if (!(option instanceof Element)) return;
      if (!(this.button instanceof Element) || !(this.listbox instanceof Element)) return;

      // ensure current selected is valid
      if (!(this.selectedOption instanceof Element)) {
        this.selectedOption = this.resolveSelectedOption();
      }

      if (option !== this.selectedOption && this.selectedOption instanceof Element) {
        option.setAttribute('aria-selected', 'true');
        this.selectedOption.setAttribute('aria-selected', 'false');

        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
          const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
          if (nativeSwatchColor) {
            this.button.style.setProperty('--native-swatch-color', nativeSwatchColor);
            this.button.style.setProperty('--native-swatch-image', '');
          }
          const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');
          if (nativeSwatchImage) {
            this.button.style.setProperty('--native-swatch-image', nativeSwatchImage);
            this.button.style.setProperty('--native-swatch-color', '');
          }
        }

        if (this.button.firstElementChild && option.firstElementChild) {
          this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        }

        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = option;

        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const detail = { selectedValue: option.dataset.value };
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail }));
        }

        requestAnimationFrame(() => this.setButtonWidth());
      }

      this.hideListbox();
    }

    disconnectedCallback() {
      if (this._markupObserver) {
        this._markupObserver.disconnect();
        this._markupObserver = null;
      }
    }
  }

  customElements.define('custom-select', CustomSelect);
}